import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { fetchCustomPersonnel, buildCustomPersonnelExcelUrl } from "../../services/webReportsService";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";

// Label dictionary mapping fields to readable titles
const fieldLabels = {
  employee_id: "Employee ID",
  staff_code: "Staff Code",
  full_name: "Staff Name",
  salutation: "Salutation",
  gender: "Gender",
  date_of_birth: "Date of Birth",
  age: "Age",
  nationality: "Nationality",
  religion: "Religion",
  race: "Race",
  marital_status: "Marital Status",
  blood_type: "Blood Type",
  father_name: "Father's Name",
  dept_name: "Dept",
  branch_name: "Branch",
  employment_status: "Status",
  work_type: "Work Type",
  joining_date: "Joining Date",
  confirmation_date: "Confirmation Date",
  cessation_date: "Cessation Date",
  nrc_number: "NRC Number",
  passport_number: "Passport Number",
  ssb_number: "SSB Number",
  bank_name: "Bank Name",
  bank_account_no: "Bank Account No",
  phone: "Phone",
  permanent_address: "Permanent Address"
};

const groupedFields = [
  {
    groupName: "General / Identity",
    fields: ["employee_id", "staff_code", "full_name", "salutation", "gender"]
  },
  {
    groupName: "Demographics",
    fields: ["date_of_birth", "age", "nationality", "religion", "race", "marital_status", "blood_type", "father_name"]
  },
  {
    groupName: "Job & Status",
    fields: ["dept_name", "branch_name", "employment_status", "work_type", "joining_date", "confirmation_date", "cessation_date"]
  },
  {
    groupName: "Legal & Finance",
    fields: ["nrc_number", "passport_number", "ssb_number", "bank_name", "bank_account_no", "phone", "permanent_address"]
  }
];

export default function CustomPersonnelReport() {
  const tenant = localStorage.getItem("tenant") || "";
  const token = sessionStorage.getItem("token") || "";

  // Filter States
  const [branchId, setBranchId] = useState("");
  const [orgNodeId, setOrgNodeId] = useState("");

  // Checkbox Selection States (Default columns checked on mount)
  const [selectedFields, setSelectedFields] = useState({
    employee_id: true,
    staff_code: true,
    full_name: true,
    dept_name: true,
    branch_name: true,
    joining_date: true,
    employment_status: true
  });

  // Metadata Dropdown States
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Data Grid States
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [isFieldsPanelExpanded, setIsFieldsPanelExpanded] = useState(true);

  // Fetch branches and departments dropdown data on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      const config = {
        withCredentials: true,
        headers: {
          "X-Tenant": tenant,
          "Authorization": `Bearer ${token}`
        }
      };
      try {
        const [branchRes, nodeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
          axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
        ]);

        if (Array.isArray(branchRes.data?.data)) {
          setBranches(branchRes.data.data);
        }
        if (Array.isArray(nodeRes.data?.data)) {
          setDepartments(buildHierarchicalList(nodeRes.data.data));
        }
      } catch (err) {
        console.error("Failed to load metadata filters:", err);
      }
    };

    if (tenant && token) {
      fetchMetadata();
    }
  }, [tenant, token]);

  const activeFieldsList = useMemo(() => {
    return Object.entries(selectedFields)
      .filter(([_, checked]) => checked)
      .map(([f]) => f);
  }, [selectedFields]);

  const onSearch = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    if (activeFieldsList.length === 0) {
      setError("Please select at least one column/field to display.");
      setRows([]);
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await fetchCustomPersonnel({
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined,
        fields: activeFieldsList.join(",")
      });

      if (res.success) {
        setRows(res.rows || []);
      } else {
        throw new Error(res.message || "Failed to load report");
      }
    } catch (e) {
      setError(e?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Run initial query on mount when metadata is ready
  useEffect(() => {
    if (tenant) onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const handleCheckboxChange = (fieldName) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const onExportExcel = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    if (activeFieldsList.length === 0) {
      setError("Please select at least one column/field to export.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const url = buildCustomPersonnelExcelUrl({
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined,
        fields: activeFieldsList.join(",")
      });

      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Tenant": tenant },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Export failed: HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `custom_staff_directory_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "legal" });

      if (myanmarFontBase64) {
        doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
        doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
        doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
      }
      const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

      doc.setFont(defaultFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59);
      doc.text("Custom Staff Directory Report", 14, 15);

      doc.setFont(defaultFont, "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated At: ${new Date().toLocaleDateString()}`, 14, 21);

      // Headers and dynamic values mapping
      const tableHeaders = ["No."];
      activeFieldsList.forEach(f => {
        tableHeaders.push(fieldLabels[f] || f);
      });

      const tableRows = rows.map((r, index) => {
        const rowData = [index + 1];
        activeFieldsList.forEach(f => {
          rowData.push(r[f] !== undefined && r[f] !== null ? r[f] : "-");
        });
        return rowData;
      });

      // Calculate font scale based on number of columns
      let fontSize = 8;
      if (activeFieldsList.length > 15) fontSize = 6;
      else if (activeFieldsList.length > 10) fontSize = 7;

      autoTable(doc, {
        startY: 26,
        head: [tableHeaders],
        body: tableRows,
        theme: "striped",
        headStyles: { fillColor: [79, 70, 229], font: defaultFont, fontStyle: "bold" },
        styles: { fontSize, font: defaultFont },
        columnStyles: {
          0: { cellWidth: 10 }
        }
      });

      doc.save(`custom_staff_directory_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      setError(e?.message || "PDF Export failed");
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <AppHeader />

      <div style={{ padding: "24px 32px", maxWidth: 1450, margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>
          Custom Staff Directory
        </h2>
        <p style={{ color: "#64748b", margin: "0 0 24px 0" }}>
          Generate tailor-made personnel directories by selecting custom fields, filters, and formats.
        </p>

        {/* Field Selectors Checkbox Panel */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: "20px 24px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
          marginBottom: 20
        }}>
          <div 
            onClick={() => setIsFieldsPanelExpanded(prev => !prev)}
            style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              cursor: "pointer", 
              userSelect: "none",
              marginBottom: isFieldsPanelExpanded ? 16 : 0
            }}
          >
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 800, color: "#334155", display: "flex", alignItems: "center", gap: 8 }}>
              Select Fields to Include:
              <span style={{ fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                ({activeFieldsList.length} selected)
              </span>
            </h4>
            <span style={{ 
              fontSize: "13px", 
              fontWeight: 600, 
              color: "primary.dark", 
              display: "flex", 
              alignItems: "center", 
              gap: 4 
            }}>
              {isFieldsPanelExpanded ? "Collapse ▲" : "Expand Fields ▼"}
            </span>
          </div>

          {isFieldsPanelExpanded && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {groupedFields.map(group => (
              <div key={group.groupName} style={{ borderRight: "1px solid #f1f5f9", paddingRight: 12 }}>
                <span style={{ display: "block", fontSize: "12px", fontWeight: 800, color: "primary.dark", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {group.groupName}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {group.fields.map(fieldName => (
                    <label key={fieldName} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", color: "#334155", cursor: "pointer", userSelect: "none" }}>
                      <input 
                        type="checkbox" 
                        checked={Boolean(selectedFields[fieldName])} 
                        onChange={() => handleCheckboxChange(fieldName)} 
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      {fieldLabels[fieldName] || fieldName}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>

        {/* Filters and Action Bar */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: "20px 24px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24
        }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "end" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Branch</label>
              <select 
                value={branchId} 
                onChange={(e) => setBranchId(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 180 }}
              >
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Department</label>
              <select 
                value={orgNodeId} 
                onChange={(e) => setOrgNodeId(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 180 }}
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>
                    {' '.repeat((d._depth || 0) * 2)}{d._depth > 0 ? '└ ' : ''}{d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button 
              onClick={onSearch} 
              disabled={loading}
              style={{
                backgroundColor: "primary.dark",
                color: "#ffffff",
                padding: "8px 18px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: loading ? 0.6 : 1
              }}
            >
              Search
            </button>
            <button 
              onClick={onExportExcel} 
              disabled={loading || rows.length === 0}
              style={{
                backgroundColor: "#10b981",
                color: "#ffffff",
                padding: "8px 18px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: (loading || rows.length === 0) ? 0.6 : 1
              }}
            >
              Export Excel
            </button>
            <button 
              onClick={onExportPDF} 
              disabled={loading || rows.length === 0}
              style={{
                backgroundColor: "#e11d48",
                color: "#ffffff",
                padding: "8px 18px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: (loading || rows.length === 0) ? 0.6 : 1
              }}
            >
              Print PDF
            </button>
          </div>
        </div>

        {/* PDF limit Column Alert Warning */}
        {activeFieldsList.length > 10 && (
          <div style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            color: "#b45309",
            padding: "12px 16px",
            borderRadius: 6,
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: 16
          }}>
            ⚠️ You have selected {activeFieldsList.length} fields. For PDF/Print formats, choosing more than 10 columns might result in very compact formatting. If printing is required, try reducing selected fields or use <strong>Export Excel</strong> for best formatting.
          </div>
        )}

        {error && <div style={{ marginBottom: 16, color: "#ef4444", fontSize: "14px", fontWeight: 600 }}>{error}</div>}
        {loading && <div style={{ marginBottom: 16, color: "primary.dark", fontSize: "14px" }}>Loading directory data...</div>}

        {/* Data Grid Preview Table */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#334155" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>No.</th>
                  {activeFieldsList.map(f => (
                    <th key={f} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>
                      {fieldLabels[f] || f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.staff_id || index} style={{ borderBottom: "1px solid #f1f5f9", hover: { backgroundColor: "#f8fafc" } }}>
                    <td style={{ padding: "10px 16px", borderRight: "1px solid #e2e8f0" }}>{index + 1}</td>
                    {activeFieldsList.map(f => (
                      <td key={f} style={{ padding: "10px 16px", borderRight: "1px solid #e2e8f0" }}>
                        {r[f] !== undefined && r[f] !== null ? r[f] : "-"}
                      </td>
                    ))}
                  </tr>
                ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={activeFieldsList.length + 1} style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
                      No matching staff records found. Choose fields and click Search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
